import styled from "styled-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRocket, faMagic, faHeart, faCheckCircle } from "@fortawesome/free-solid-svg-icons"

const AutoStatsFeature = () => {
    return (
        <FeatureSection>
            <Container className="container">
                <Badge>
                    <FontAwesomeIcon icon={faRocket} className="me-2" />
                    GAME CHANGER
                </Badge>

                <MainHeading className="heading">
                    Your Stats, Automatically.
                    <GlowText> Finally.</GlowText>
                </MainHeading>

                <SubHeading>
                    No more manual entry. No more waiting. No more fake results ruining your leaderboard.
                    Just play your matches and watch your stats appear like magic.
                </SubHeading>

                <FeaturesGrid className="row g-4 mt-4">
                    <div className="col-md-4">
                        <FeatureCard>
                            <IconWrapper>
                                <FontAwesomeIcon icon={faMagic} size="2x" />
                            </IconWrapper>
                            <FeatureTitle>Play. Done.</FeatureTitle>
                            <FeatureText>
                                Finish your match, grab a drink, and boom—your stats are already live.
                                No forms. No screenshots. No hassle.
                            </FeatureText>
                        </FeatureCard>
                    </div>

                    <div className="col-md-4">
                        <FeatureCard>
                            <IconWrapper>
                                <FontAwesomeIcon icon={faHeart} size="2x" />
                            </IconWrapper>
                            <FeatureTitle>Fair & Clean</FeatureTitle>
                            <FeatureText>
                                We caught what other leagues missed. Rage quits don't count.
                                Your leaderboard shows real skills, not fake victories.
                            </FeatureText>
                        </FeatureCard>
                    </div>

                    <div className="col-md-4">
                        <FeatureCard>
                            <IconWrapper>
                                <FontAwesomeIcon icon={faCheckCircle} size="2x" />
                            </IconWrapper>
                            <FeatureTitle>Never Miss a Beat</FeatureTitle>
                            <FeatureText>
                                Draw? Technical issue? No worries. Quick manual backup
                                means your match always counts. We've got you covered.
                            </FeatureText>
                        </FeatureCard>
                    </div>
                </FeaturesGrid>

                <ComparisonSection className="mt-5">
                    <ComparisonTitle>Why Players Love Us</ComparisonTitle>
                    <ComparisonGrid className="row mt-4">
                        <div className="col-md-6">
                            <OtherLeaguesCard>
                                <CardLabel>Other Leagues</CardLabel>
                                <ComparisonPoint negative>
                                    <CrossIcon>✗</CrossIcon>
                                    <span>Fake 3-0s everywhere</span>
                                </ComparisonPoint>
                                <ComparisonPoint negative>
                                    <CrossIcon>✗</CrossIcon>
                                    <span>Standing still to force draws</span>
                                </ComparisonPoint>
                                <ComparisonPoint negative>
                                    <CrossIcon>✗</CrossIcon>
                                    <span>Lost matches = lost stats</span>
                                </ComparisonPoint>
                            </OtherLeaguesCard>
                        </div>
                        <div className="col-md-6">
                            <UsCard>
                                <CardLabel>VGC Experience</CardLabel>
                                <ComparisonPoint positive>
                                    <CheckIcon>✓</CheckIcon>
                                    <span>Legit stats only</span>
                                </ComparisonPoint>
                                <ComparisonPoint positive>
                                    <CheckIcon>✓</CheckIcon>
                                    <span>Play naturally, we handle it</span>
                                </ComparisonPoint>
                                <ComparisonPoint positive>
                                    <CheckIcon>✓</CheckIcon>
                                    <span>Every match counts</span>
                                </ComparisonPoint>
                            </UsCard>
                        </div>
                    </ComparisonGrid>
                </ComparisonSection>

                <TestimonialSection>
                    <TestimonialText>
                        "Finally, a league that gets it right. No more babysitting my stats!"
                    </TestimonialText>
                    <TestimonialAuthor>— Happy VGC Player</TestimonialAuthor>
                </TestimonialSection>

                <CTASection>
                    <CTAText>
                        Join 30+ teams who never worry about stats again
                    </CTAText>
                    <CTAButton href="#hero">
                        Start Playing Better
                        <FontAwesomeIcon icon={faRocket} className="ms-2" />
                    </CTAButton>
                </CTASection>
            </Container>
        </FeatureSection>
    )
}

export default AutoStatsFeature

const FeatureSection = styled.section`
    padding: 4rem 1rem;
    background: linear-gradient(135deg, #0a0a1f 0%, #1a0f2e 50%, #0a0a1f 100%);
    border-top: 2px solid #aeeaff;
    border-bottom: 2px solid #aeeaff;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at 50% 50%, rgba(174, 234, 255, 0.05) 0%, transparent 50%);
        pointer-events: none;
    }
`

const Container = styled.div`
    position: relative;
    z-index: 1;
`

const Badge = styled.div`
    display: inline-block;
    background: linear-gradient(135deg, #aeeaff 0%, #00d4ff 100%);
    color: #0a0a1f;
    padding: 0.5rem 1.5rem;
    border-radius: 50px;
    font-weight: bold;
    font-size: 0.875rem;
    letter-spacing: 0.1em;
    margin-bottom: 1.5rem;
    box-shadow: 0 0 20px rgba(174, 234, 255, 0.5);
    animation: pulse 2s ease-in-out infinite;

    @keyframes pulse {
        0%, 100% { box-shadow: 0 0 20px rgba(174, 234, 255, 0.5); }
        50% { box-shadow: 0 0 30px rgba(174, 234, 255, 0.8); }
    }
`

const MainHeading = styled.h1`
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 1rem;
    line-height: 1.2;
`

const GlowText = styled.span`
    background: linear-gradient(135deg, #aeeaff 0%, #00d4ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 10px rgba(174, 234, 255, 0.5));
`

const SubHeading = styled.p`
    font-size: clamp(1rem, 2vw, 1.25rem);
    color: #b0b0c8;
    max-width: 800px;
    line-height: 1.6;
    margin-bottom: 2rem;
`

const FeaturesGrid = styled.div`
    margin-top: 3rem;
`

const FeatureCard = styled.div`
    background: rgba(24, 24, 48, 0.6);
    border: 1px solid rgba(174, 234, 255, 0.2);
    border-radius: 12px;
    padding: 2rem;
    height: 100%;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);

    &:hover {
        border-color: #aeeaff;
        box-shadow: 0 0 20px rgba(174, 234, 255, 0.3);
        transform: translateY(-5px);
    }
`

const IconWrapper = styled.div`
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #aeeaff 0%, #00d4ff 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    color: #0a0a1f;
    box-shadow: 0 4px 15px rgba(174, 234, 255, 0.3);
`

const FeatureTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 1rem;
`

const FeatureText = styled.p`
    color: #b0b0c8;
    line-height: 1.6;
    margin: 0;
    font-size: 1.05rem;
`

const ComparisonSection = styled.div`
    margin-top: 4rem;
    padding-top: 3rem;
    border-top: 1px solid rgba(174, 234, 255, 0.2);
`

const ComparisonTitle = styled.h2`
    font-size: 2rem;
    font-weight: 700;
    color: #ffffff;
    text-align: center;
    margin-bottom: 1rem;
`

const ComparisonGrid = styled.div``

const OtherLeaguesCard = styled.div`
    background: rgba(48, 24, 24, 0.6);
    border: 1px solid rgba(255, 100, 100, 0.3);
    border-radius: 12px;
    padding: 2rem;
    height: 100%;
`

const UsCard = styled.div`
    background: rgba(24, 48, 48, 0.6);
    border: 1px solid rgba(174, 234, 255, 0.3);
    border-radius: 12px;
    padding: 2rem;
    height: 100%;
    box-shadow: 0 0 15px rgba(174, 234, 255, 0.2);
`

const CardLabel = styled.div`
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 1.5rem;
    color: #b0b0c8;
`

const ComparisonPoint = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  color: ${props => props.positive ? '#aeeaff' : '#ff6b6b'};
  font-size: 1.05rem;
`

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(174, 234, 255, 0.2);
  color: #aeeaff;
  font-weight: bold;
  flex-shrink: 0;
`

const CrossIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 100, 100, 0.2);
  color: #ff6b6b;
  font-weight: bold;
  flex-shrink: 0;
`

const TestimonialSection = styled.div`
  text-align: center;
  margin-top: 4rem;
  padding: 2rem;
`

const TestimonialText = styled.p`
  font-size: 1.5rem;
  font-style: italic;
  color: #ffffff;
  margin-bottom: 1rem;
  line-height: 1.6;
`

const TestimonialAuthor = styled.p`
  color: #b0b0c8;
  font-size: 1rem;
`

const CTASection = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding: 2rem;
  background: rgba(174, 234, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(174, 234, 255, 0.2);
`

const CTAText = styled.p`
  font-size: 1.25rem;
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-weight: 500;
`

const CTAButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #aeeaff 0%, #00d4ff 100%);
  color: #0a0a1f;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1.125rem;
  text-decoration: none;
  box-shadow: 0 0 30px rgba(174, 234, 255, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 40px rgba(174, 234, 255, 0.6);
    color: #0a0a1f;
  }
`